/**
 * ProductFoamBomConfigTable.tsx
 *
 * Config list table for Product Foam / Packaging BOM.
 * Placed directly underneath the wood/hardware BOM table on ProductDetailPage.
 */
import { useState } from 'react'
import { FolderOpen, Package, Plus, Trash2 } from 'lucide-react'
import type { FoamBomConfig, FoamAssemblyType, FoamPackagingType } from '../types/foamBomTypes'
import {
  createBlankFoamBomConfig,
  FOAM_ASSEMBLY_OPTIONS,
  FOAM_PACKAGING_OPTIONS,
  getFoamAssemblyLabel,
  getFoamPackagingLabel,
} from '../types/foamBomTypes'

interface ProductFoamBomConfigTableProps {
  productCode: string
  configs: FoamBomConfig[]
  onChange: (configs: FoamBomConfig[]) => void
  onOpenBom: (config: FoamBomConfig) => void
}

export function ProductFoamBomConfigTable({
  productCode,
  configs,
  onChange,
  onOpenBom,
}: ProductFoamBomConfigTableProps) {
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState<number | null>(null)

  function handleAddRow() {
    const newConfig = createBlankFoamBomConfig(configs)
    onChange([...configs, newConfig])
  }

  function handleAssemblyChange(idx: number, val: FoamAssemblyType) {
    const updated = [...configs]
    updated[idx] = { ...updated[idx], assembly_type: val }
    onChange(updated)
  }

  function handlePackagingChange(idx: number, val: FoamPackagingType) {
    const updated = [...configs]
    updated[idx] = { ...updated[idx], packaging_type: val }
    onChange(updated)
  }

  function handleStatusChange(idx: number, val: 'OK' | 'Đang Cập Nhật') {
    const updated = [...configs]
    updated[idx] = { ...updated[idx], status: val }
    onChange(updated)
  }

  function confirmDeleteRow() {
    if (deleteConfirmIdx === null) return
    const updated = configs.filter((_, i) => i !== deleteConfirmIdx)
    onChange(updated)
    setDeleteConfirmIdx(null)
  }

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)] p-4">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded bg-blue-100 text-blue-700">
            <Package className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">
              BOM ĐÓNG GÓI
            </h3>
            <p className="text-[11px] text-slate-400">
              Quản lý định mức vật tư đóng gói (mút xốp, carton, phụ kiện bảo vệ) theo kết cấu & hình thức đóng gói
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddRow}
          className="inline-flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:bg-blue-700 active:scale-[0.98]"
        >
          <Plus className="h-3.5 w-3.5" />
          + Tạo dòng cấu hình BOM Đóng Gói
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-600">
              <th className="px-3 py-2.5" style={{ width: '130px' }}>
                MÃ HÀNG
              </th>
              <th className="px-3 py-2.5" style={{ minWidth: '180px' }}>
                KẾT CẤU
              </th>
              <th className="px-3 py-2.5" style={{ minWidth: '180px' }}>
                ĐÓNG GÓI
              </th>
              <th className="px-3 py-2.5 text-center" style={{ width: '150px' }}>
                TRẠNG THÁI
              </th>
              <th className="px-3 py-2.5 text-center" style={{ width: '160px' }}>
                THAO TÁC
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {configs.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-8 text-center text-xs text-slate-400"
                >
                  Chưa có cấu hình BOM Đóng Gói nào cho mã hàng này. Nhấp vào "+ Tạo dòng cấu hình BOM Đóng Gói" để bắt đầu thiết lập.
                </td>
              </tr>
            ) : (
              configs.map((cfg, idx) => (
                <tr key={cfg.id || idx} className="hover:bg-slate-50/70 transition-colors">
                  {/* Cột 1: Mã hàng */}
                  <td className="px-3 py-2.5 font-mono text-xs font-bold text-slate-800">
                    <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-slate-900 border border-slate-200">
                      {productCode}
                    </span>
                  </td>

                  {/* Cột 2: Kết cấu */}
                  <td className="px-3 py-2.5">
                    <select
                      value={cfg.assembly_type}
                      onChange={(e) => handleAssemblyChange(idx, e.target.value as FoamAssemblyType)}
                      className="w-full rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {FOAM_ASSEMBLY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Cột 3: Đóng gói */}
                  <td className="px-3 py-2.5">
                    <select
                      value={cfg.packaging_type}
                      onChange={(e) => handlePackagingChange(idx, e.target.value as FoamPackagingType)}
                      className="w-full rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {FOAM_PACKAGING_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Cột 4: Trạng thái */}
                  <td className="px-3 py-2.5 text-center">
                    <select
                      value={cfg.status || 'Đang Cập Nhật'}
                      onChange={(e) => handleStatusChange(idx, e.target.value as 'OK' | 'Đang Cập Nhật')}
                      className={`rounded px-2 py-1 text-xs font-bold border transition ${
                        cfg.status === 'OK'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                          : 'border-amber-300 bg-amber-50 text-amber-800'
                      }`}
                    >
                      <option value="OK">● OK</option>
                      <option value="Đang Cập Nhật">⚡ Đang Cập Nhật</option>
                    </select>
                  </td>

                  {/* Cột 5: Thao tác */}
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => onOpenBom(cfg)}
                        className="inline-flex items-center gap-1 rounded bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white shadow-xs transition hover:bg-blue-700 active:scale-95"
                        title="Mở bảng BOM Đóng Gói chi tiết"
                      >
                        <FolderOpen className="h-3.5 w-3.5" />
                        Mở bảng
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmIdx(idx)}
                        className="rounded p-1 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                        title="Xóa cấu hình này"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl border border-slate-200">
            <h4 className="text-sm font-bold text-slate-900 mb-2">
              Xác nhận xóa cấu hình BOM Đóng Gói
            </h4>
            <p className="text-xs text-slate-600 mb-4">
              Bạn có chắc chắn muốn xóa cấu hình{' '}
              <span className="font-semibold text-slate-800">
                {getFoamAssemblyLabel(configs[deleteConfirmIdx]?.assembly_type)} - {getFoamPackagingLabel(configs[deleteConfirmIdx]?.packaging_type)}
              </span>
              ? Dữ liệu bảng tính kèm theo cấu hình này sẽ bị xóa.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmIdx(null)}
                className="rounded border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={confirmDeleteRow}
                className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
              >
                Xóa cấu hình
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
